import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Tooltip, Avatar, List, ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction, useTheme, useMediaQuery
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/AddOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import PersonIcon from '@mui/icons-material/PersonOutline';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import WorkIcon from '@mui/icons-material/WorkOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoneyOutlined';
import api from '../api';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const ContractorManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [newContractor, setNewContractor] = useState({
    name: '',
    phone: '',
    email: '',
    specialties: [],
    hourlyRate: '',
    address: '',
    licenseNumber: ''
  });

  const specialties = [
    'Plumbing', 'Electrical', 'HVAC', 'Carpentry', 'Painting', 
    'Roofing', 'Landscaping', 'Cleaning', 'General Maintenance'
  ];

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      const response = await api.get('/api/contractors');
      setContractors(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to fetch contractors', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddContractor = async () => {
    try {
      await api.post('/api/contractors', {
        ...newContractor,
        hourlyRate: parseFloat(newContractor.hourlyRate) || 0
      });
      setSnackbar({ open: true, message: 'Contractor added successfully!', severity: 'success' });
      setAddDialog(false);
      setNewContractor({
        name: '',
        phone: '',
        email: '',
        specialties: [],
        hourlyRate: '',
        address: '',
        licenseNumber: ''
      });
      fetchContractors();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to add contractor', severity: 'error' });
    }
  };

  const handleEditContractor = async () => {
    try {
      await api.put(`/api/contractors/${selectedContractor.id}`, {
        ...selectedContractor,
        hourlyRate: parseFloat(selectedContractor.hourlyRate) || 0
      });
      setSnackbar({ open: true, message: 'Contractor updated successfully!', severity: 'success' });
      setEditDialog(false);
      setSelectedContractor(null);
      fetchContractors();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update contractor', severity: 'error' });
    }
  };

  const handleDeleteContractor = async (contractorId) => {
    if (!window.confirm('Are you sure you want to delete this contractor?')) return;
    try {
      await api.delete(`/api/contractors/${contractorId}`);
      setSnackbar({ open: true, message: 'Contractor deleted successfully!', severity: 'success' });
      fetchContractors();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete contractor', severity: 'error' });
    }
  };

  const handleEdit = (contractor) => {
    setSelectedContractor(contractor);
    setEditDialog(true);
  };

  const handleSpecialtyToggle = (specialty, isEdit = false) => {
    const target = isEdit ? selectedContractor : newContractor;
    const setTarget = isEdit ? setSelectedContractor : setNewContractor;
    
    const currentSpecialties = target.specialties || [];
    const newSpecialties = currentSpecialties.includes(specialty)
      ? currentSpecialties.filter(s => s !== specialty)
      : [...currentSpecialties, specialty];
    
    setTarget({ ...target, specialties: newSpecialties });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 3, md: 4 } }}><Typography>Loading contractors...</Typography></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 1, sm: 0 } }}
        >
          <WorkIcon sx={{ mr: { xs: 0.5, sm: 1 }, verticalAlign: 'middle' }} />
          {isMobile ? 'Contractors' : 'Contractor Management'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialog(true)}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          {isMobile ? 'Add' : 'Add Contractor'}
        </Button>
      </Box>

      {contractors.length === 0 && (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
            <WorkIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No contractors yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 440, mx: 'auto' }}>
              Add the plumbers, electricians and handypeople you trust. You can then assign them to
              maintenance requests and track their costs in one place.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialog(true)}>
              Add your first contractor
            </Button>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {contractors.map((contractor) => (
          <Grid item xs={12} md={6} lg={4} key={contractor.id}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start', 
                  mb: { xs: 1.5, sm: 2 },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <Avatar sx={{ mr: { xs: 1, sm: 2 }, width: { xs: 40, sm: 56 }, height: { xs: 40, sm: 56 } }}>
                      <PersonIcon />
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography 
                        variant="h6"
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                        noWrap
                      >
                        {contractor.name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        License: {contractor.licenseNumber || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                    <Tooltip title="Edit Contractor">
                      <IconButton
                        size={isMobile ? 'small' : 'medium'}
                        onClick={() => handleEdit(contractor)}
                        aria-label="Edit contractor"
                      >
                        <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Contractor">
                      <IconButton
                        size={isMobile ? 'small' : 'medium'}
                        color="error"
                        onClick={() => handleDeleteContractor(contractor.id)}
                        aria-label="Delete contractor"
                      >
                        <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <List dense sx={{ py: 0 }}>
                  <ListItem sx={{ px: 0, py: { xs: 0.5, sm: 1 } }}>
                    <ListItemAvatar>
                      <Avatar sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                        <PhoneIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary="Phone" 
                      secondary={contractor.phone}
                      primaryTypographyProps={{ sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
                      secondaryTypographyProps={{ sx: { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: { xs: 0.5, sm: 1 } }}>
                    <ListItemAvatar>
                      <Avatar sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                        <EmailIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary="Email" 
                      secondary={contractor.email}
                      primaryTypographyProps={{ sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
                      secondaryTypographyProps={{ sx: { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: { xs: 0.5, sm: 1 } }}>
                    <ListItemAvatar>
                      <Avatar sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                        <AttachMoneyIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary="Hourly Rate" 
                      secondary={`$${contractor.hourlyRate}/hour`}
                      primaryTypographyProps={{ sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
                      secondaryTypographyProps={{ sx: { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                    />
                  </ListItem>
                </List>

                <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: { xs: 0.5, sm: 1 } }}
                  >
                    Specialties:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 0.5 }, flexWrap: 'wrap' }}>
                    {(contractor.specialties || []).map((specialty) => (
                      <Chip 
                        key={specialty} 
                        label={specialty} 
                        size="small" 
                        color="primary"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    ))}
                  </Box>
                </Box>

                {contractor.address && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mt: { xs: 1, sm: 1 },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      wordBreak: 'break-word'
                    }}
                  >
                    <strong>Address:</strong> {contractor.address}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add Contractor Dialog */}
      <Dialog 
        open={addDialog} 
        onClose={() => setAddDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Add New Contractor</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            label="Name"
            value={newContractor.name}
            onChange={(e) => setNewContractor({ ...newContractor, name: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Phone"
            value={newContractor.phone}
            onChange={(e) => setNewContractor({ ...newContractor, phone: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Email"
            value={newContractor.email}
            onChange={(e) => setNewContractor({ ...newContractor, email: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Hourly Rate"
            value={newContractor.hourlyRate}
            onChange={(e) => setNewContractor({ ...newContractor, hourlyRate: e.target.value })}
            fullWidth
            margin="dense"
            type="number"
          />
          <TextField
            label="Address"
            value={newContractor.address}
            onChange={(e) => setNewContractor({ ...newContractor, address: e.target.value })}
            fullWidth
            margin="dense"
            multiline
            rows={2}
          />
          <TextField
            label="License Number"
            value={newContractor.licenseNumber}
            onChange={(e) => setNewContractor({ ...newContractor, licenseNumber: e.target.value })}
            fullWidth
            margin="dense"
          />
          <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
            <Typography 
              variant="subtitle2" 
              gutterBottom
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: { xs: 0.5, sm: 1 } }}
            >
              Specialties:
            </Typography>
            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 0.5 }, flexWrap: 'wrap' }}>
              {specialties.map((specialty) => (
                <Chip
                  key={specialty}
                  label={specialty}
                  onClick={() => handleSpecialtyToggle(specialty)}
                  color={newContractor.specialties.includes(specialty) ? 'primary' : 'default'}
                  variant={newContractor.specialties.includes(specialty) ? 'filled' : 'outlined'}
                  clickable
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ fontSize: { xs: '0.65rem', sm: '0.875rem' } }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setAddDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddContractor} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Add Contractor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contractor Dialog */}
      <Dialog 
        open={editDialog} 
        onClose={() => setEditDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Edit Contractor</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {selectedContractor && (
            <>
              <TextField
                label="Name"
                value={selectedContractor.name}
                onChange={(e) => setSelectedContractor({ ...selectedContractor, name: e.target.value })}
                fullWidth
                margin="dense"
              />
              <TextField
                label="Phone"
                value={selectedContractor.phone}
                onChange={(e) => setSelectedContractor({ ...selectedContractor, phone: e.target.value })}
                fullWidth
                margin="dense"
              />
              <TextField
                label="Email"
                value={selectedContractor.email}
                onChange={(e) => setSelectedContractor({ ...selectedContractor, email: e.target.value })}
                fullWidth
                margin="dense"
              />
              <TextField
                label="Hourly Rate"
                value={selectedContractor.hourlyRate}
                onChange={(e) => setSelectedContractor({ ...selectedContractor, hourlyRate: e.target.value })}
                fullWidth
                margin="dense"
                type="number"
              />
              <TextField
                label="Address"
                value={selectedContractor.address}
                onChange={(e) => setSelectedContractor({ ...selectedContractor, address: e.target.value })}
                fullWidth
                margin="dense"
                multiline
                rows={2}
              />
              <TextField
                label="License Number"
                value={selectedContractor.licenseNumber}
                onChange={(e) => setSelectedContractor({ ...selectedContractor, licenseNumber: e.target.value })}
                fullWidth
                margin="dense"
              />
              <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                <Typography 
                  variant="subtitle2" 
                  gutterBottom
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: { xs: 0.5, sm: 1 } }}
                >
                  Specialties:
                </Typography>
                <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 0.5 }, flexWrap: 'wrap' }}>
                  {specialties.map((specialty) => (
                    <Chip
                      key={specialty}
                      label={specialty}
                      onClick={() => handleSpecialtyToggle(specialty, true)}
                      color={(selectedContractor.specialties || []).includes(specialty) ? 'primary' : 'default'}
                      variant={(selectedContractor.specialties || []).includes(specialty) ? 'filled' : 'outlined'}
                      clickable
                      size={isMobile ? 'small' : 'medium'}
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.875rem' } }}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setEditDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEditContractor} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Update Contractor
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContractorManagement; 